/**
 * Admin Dashboard Component
 * Main dashboard overview for VarsityLink administrators
 */

import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Building, 
  FileText, 
  Briefcase, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Clock,
  MessageSquare,
  UserPlus,
  Eye,
  Download
} from 'lucide-react';
import { getDashboardStats } from '../services/adminService';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardStats = await getDashboardStats();
      setStats(dashboardStats);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Failed to load dashboard statistics. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    // Placeholder for data export functionality
    console.log('Exporting data...');
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="animate-pulse">
          <div className="h-6 sm:h-8 bg-gray-200 rounded w-1/2 sm:w-1/4 mb-4 sm:mb-6"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-6 sm:h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-2 sm:h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="h-8 w-8 sm:h-10 sm:w-10 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to Load Dashboard</h3>
          <p className="text-red-700 text-sm sm:text-base mb-4">{error}</p>
          <button
            onClick={fetchDashboardStats}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg transition-colors text-sm sm:text-base font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">VarsityLink System Overview</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="w-full sm:w-auto border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button
            onClick={handleExportData}
            className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
          >
            <Download size={16} />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Total Users */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{stats?.users?.total || 0}</p>
              <p className="text-sm text-green-600">+{stats?.users?.newThisMonth || 0} this month</p>
            </div>
            <div className="bg-green-100 p-2 sm:p-3 rounded-full flex-shrink-0">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Total Companies */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">Companies</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{stats?.companies?.total || 0}</p>
              <p className="text-sm text-orange-600">{stats?.companies?.pending || 0} pending verification</p>
            </div>
            <div className="bg-green-100 p-2 sm:p-3 rounded-full flex-shrink-0">
              <Building className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Job Postings */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">Job Postings</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{stats?.jobs?.total || 0}</p>
              <p className="text-sm text-green-600">{stats?.jobs?.active || 0} active</p>
            </div>
            <div className="bg-green-100 p-2 sm:p-3 rounded-full flex-shrink-0">
              <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Resources */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">Resources</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{stats?.resources?.total || 0}</p>
              <p className="text-sm text-yellow-600">{stats?.resources?.pending || 0} pending review</p>
            </div>
            <div className="bg-green-100 p-2 sm:p-3 rounded-full flex-shrink-0">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Social Engagement</h3>
            <MessageSquare className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Messages</span>
              <span className="font-medium">{stats?.engagement?.totalMessages || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">University Distribution</h3>
            <TrendingUp className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {Object.entries(stats?.users?.byUniversity || {})
              .slice(0, 5)
              .map(([university, count]) => (
                <div key={university} className="flex justify-between">
                  <span className="text-sm text-gray-600 truncate">{university}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Company Industries</h3>
            <Building className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {Object.entries(stats?.companies?.byIndustry || {})
              .slice(0, 5)
              .map(([industry, count]) => (
                <div key={industry} className="flex justify-between">
                  <span className="text-sm text-gray-600 truncate">{industry}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <button className="flex items-center space-x-3 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm sm:text-base">Pending Companies</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">{stats?.companies?.pending || 0} awaiting verification</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <div className="bg-yellow-100 p-2 rounded-lg flex-shrink-0">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm sm:text-base">Pending Resources</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">{stats?.resources?.pending || 0} awaiting review</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <div className="bg-red-100 p-2 rounded-lg flex-shrink-0">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm sm:text-base">Reported Content</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">0 items to review</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
              <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm sm:text-base">System Health</p>
              <p className="text-xs sm:text-sm text-green-600 truncate">All systems operational</p>
            </div>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center space-x-3 sm:space-x-4 p-3 bg-gray-50 rounded-lg">
            <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
              <UserPlus className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">New user registered</p>
              <p className="text-xs text-gray-600 truncate">University of Cape Town - 2 minutes ago</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4 p-3 bg-gray-50 rounded-lg">
            <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
              <Building className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Company verification pending</p>
              <p className="text-xs text-gray-600 truncate">TechCorp Solutions - 15 minutes ago</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4 p-3 bg-gray-50 rounded-lg">
            <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
              <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Resource uploaded for review</p>
              <p className="text-xs text-gray-600 truncate">Computer Science Notes - 1 hour ago</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4 p-3 bg-gray-50 rounded-lg">
            <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
              <Briefcase className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">New job posting created</p>
              <p className="text-xs text-gray-600 truncate">Software Developer Intern - 2 hours ago</p>
            </div>
          </div>
        </div>
              </div>
    </div>
  );
};

export default AdminDashboard;